import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import { LoadingType, setAccount, setLoadingState } from 'prequalificationV2/actions';
import { VerificationState } from 'api/TruvApi';
import { Button } from 'components/Buttons/Button';
import DashboardFormHeader from 'components/DashboardFormHeader';
import DashboardTopInfo from 'components/DashboardTopInfo';
import Loader from 'components/Loader';
import Sidebar, { SidebarElement } from 'components/Sidebar';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { ApplicationType } from 'enums/ApplicationType';
import { BorrowerRole } from 'enums/BorrowerRole';
import { BorrowerType } from 'enums/BorrowerType';
import { ButtonType } from 'enums/ButtonType';
import { DashboardSidebarElements } from 'enums/DashboardSidebarElements';
import { DashboardStep } from 'enums/DashboardStep';
import { DocumentCategory } from 'enums/DocumentCategory';
import { KbaStatus } from 'enums/ApplicationsVariables';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import DeclineOfferPopup from 'prequalificationV2/pages/Dashboard/DeclineOfferPopup';
import NotFoundContact from 'prequalificationV2/pages/NotFound/NotFoundContact';
import { getAccount } from 'prequalificationV2/thunks';
import { getApplicationInfoForDashboardThunk } from 'prequalificationV2/thunks/getApplicationInfoForDashboard';
import { calculateDueDate } from 'prequalificationV2/utils/calculateDueDate';
import { TRUV_DISABLED_STATUS } from 'prequalificationV2/utils/constants';
import {
  getCountNotCompletedRequiredDocuments,
  getCountBorrowerRequiredDocuments,
  isRequiredDocumentsBelongsToCategory,
  isDocumentCategoryCompleted,
} from 'prequalificationV2/utils/documentsUtils';
import { ApplicationProductName } from 'enums/common.enums';
import {
  getCompleteInformationDueDateMessage,
  getCurrentLabel,
} from 'prequalificationV2/pages/Dashboard/dashboard.utils';
import { DashboardTabName } from 'enums/DashboardTabName';
import { BorrowerInfo } from 'prequalificationV2/pages/Dashboard/dashboard.types';
import AcceptOneTermOffer from 'prequalificationV2/pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/AcceptOneTermOffer';
import { updateApplicationInStore } from 'prequalificationV2/utils/updateApplicationInStore';
import AdditionalInformationTab from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab';
import { getIsEmploymentSectionCompleted } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import LoanOfferOptions from 'prequalificationV2/pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/LoanOfferOptions/LoanOfferOptions';
import InformationalBoxWithBubble from 'components/InformationBox';
import VerificationBoxPrompt from 'components/InformationBox/VerificationBoxPrompt';
import { applicationApi } from 'prequalificationV2/api/ApplicationApi';
import BorrowerInformationTab from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/BorrowerInformationTab';
import FundingInformationTab from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/FundingInformationTab';
import NewInquiry from 'prequalificationV2/pages/Dashboard/steps/NewInquiry';
import NoActiveApplication from 'prequalificationV2/pages/Dashboard/steps/NoActiveApplication';
import HomeOwnershipTab from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipTab';
import WorkOrderTab from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/WorkOrderTab';
import AcceptMultiTermOffer from 'prequalificationV2/pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptMultiTermOffer/AcceptMultiTermOffer';
import { setDashboardStep } from 'prequalificationV2/actions/stages';
import ProsperJointTransition from 'prequalificationV2/pages/Dashboard/steps/ProsperJointTransition';
import AcceptPosAutomatedOffer from 'prequalificationV2/pages/PreQualification/steps/AcceptOffer/AutomatedOffers/AcceptPosAutomatedOffer';
import AcceptHilAutomatedOffer from 'prequalificationV2/pages/PreQualification/steps/AcceptOffer/AutomatedOffers/AcceptHilAutomatedOffer';

import {
  DASHBOARD_STEPS_WITH_UPLOADING_DOCUMENTS,
  DISABLED_FUNDING_TOOLTIP_TEXT,
  STATUSES_FOR_LOAN_DETAILS,
  STATUSES_FOR_WITHDRAW_APPLICATION,
  WITHDRAW_APPLICATION,
} from './dashboard.constants';
import styles from './Dashboard.module.scss';
import AccountSettings from './AccountSettings/AccountSettings';
import FAQ from './FAQ';
import { Tab } from './TabsPanel/TabsPanel';
import AcceptedOfferDetails from './AcceptedOfferDetails/AcceptedOfferDetails';
import MessageFromHfsPopup from './MessageFromHfsPopup';
import ContractorInformationTab from './steps/InformationTabs/ContractorInformationTab';
import ProsperLoanTab from './steps/InformationTabs/ProsperLoanTab';

const getPageByStep = (step: DashboardStep | null) => {
  switch (step) {
    case DashboardStep.NewInquiry:
      return <NewInquiry />;

    case DashboardStep.AcceptManualOneTermOffer:
      return <AcceptOneTermOffer />;

    case DashboardStep.AcceptManualMultiTermOffer:
      return <AcceptMultiTermOffer />;

    case DashboardStep.AcceptPosAutomatedOffer:
      return <AcceptPosAutomatedOffer isPrequal={false} />;

    case DashboardStep.AcceptHilAutomatedOffer:
      return <AcceptHilAutomatedOffer isPrequal={false} />;

    case DashboardStep.FundingInformation:
      return <FundingInformationTab />;

    case DashboardStep.AdditionalInformation:
      return <AdditionalInformationTab />;

    case DashboardStep.ContractorInfo:
      return <ContractorInformationTab />;

    case DashboardStep.BorrowerInformation:
      return <BorrowerInformationTab borrowerType={BorrowerRole.Borrower} />;

    case DashboardStep.CoBorrowerInformation:
      return <BorrowerInformationTab borrowerType={BorrowerRole.CoBorrower} />;

    case DashboardStep.HomeOwnership:
      return <HomeOwnershipTab />;

    case DashboardStep.ContractorWorkOrder:
      return <WorkOrderTab />;

    case DashboardStep.FAQ:
      return <FAQ />;

    case DashboardStep.NoActiveApplication:
      return <NoActiveApplication />;

    case DashboardStep.AccountSettings:
      return <AccountSettings />;

    case DashboardStep.LoanDetails:
      return <AcceptedOfferDetails />;

    case DashboardStep.NotFoundContact:
      return <NotFoundContact />;

    case DashboardStep.ProsperLoan:
      return <ProsperLoanTab />;

    case DashboardStep.ProsperJointTransition:
      return <ProsperJointTransition />;

    default:
      return <NewInquiry />;
  }
};

const getDashboardFormHeader = (
  step: DashboardStep | null,
  messageFromHFS: string | undefined,
  documentsUploadDueDate: string | null,
  tabsPanelUploadDocsAndRequireInfo: Tab[],
) => {
  switch (step) {
    case DashboardStep.BorrowerInformation:
    case DashboardStep.CoBorrowerInformation:
    case DashboardStep.FundingInformation:
    case DashboardStep.HomeOwnership:
    case DashboardStep.ContractorWorkOrder:
    case DashboardStep.AdditionalInformation:
    case DashboardStep.ContractorInfo:
      return (
        <DashboardFormHeader
          isSeparate
          mainMessage={messageFromHFS}
          infoMessage={getCompleteInformationDueDateMessage(documentsUploadDueDate)}
          tabsPanelProps={{
            dashboardTabs: tabsPanelUploadDocsAndRequireInfo,
          }}
        />
      );
  }
};

const Dashboard = () => {
  const { isIframe } = useAppSelector((state) => state.data);
  const currentDashboardStep = useAppSelector((state) => state.stages.currentDashboardStep);
  const { email } = useAppSelector((state) => state.account);
  const {
    applicationId,
    displayId,
    [LoanInformationVariables.LoanAmount]: loanAmount,
    createdDate: createdDate,
    [LoanInformationVariables.ProcessingStatusSetDate]: processingStatusSetDate,
    applicationStatus,
    [LoanInformationVariables.AssignedProcessor]: processorInfo,
    [LoanInformationVariables.LoanType]: applicationType,
    messageFromHFS,
    variables: applicationVariables,
    productName,
    messageFromHfsShowOnPortal,
    dashboardAdditionalInfo,
  } = useAppSelector((state) => state.application);
  const { firstName, lastName, borrowerId } = useAppSelector((state) => state.borrower);
  const { firstName: coBorrowerFirstName } = useAppSelector((state) => state.coBorrower);
  const {
    fundingInformationComplete,
    contractorInformationComplete,
    borrowerPayrollProviderIsDisabled,
    coBorrowerPayrollProviderIsDisabled,
    borrowerInformationRequired,
    coBorrowerInformationRequired,
    fundingInformationIsRequired,
    payrollProviderBorrowerVerificationStates,
    payrollProviderCoBorrowerVerificationStates,
    isThereNonBorrowingCoOwner,
    isHomeOwnershipInformationComplete,
    isCoOwnerInformationComplete,
    borrowerEmploymentInformationComplete,
    coBorrowerEmploymentInformationComplete,
    autopaySectionIsRequired,
    autopaySectionIsCompleted,
  } = useAppSelector((state) => state.dashboardPortal);
  const isTruvDisabled = process.env.REACT_APP_TRUV_STATUS === TRUV_DISABLED_STATUS;
  const [isDeclineOfferOpen, setIsDeclineOfferOpen] = useState(false);
  const { requiredDocuments } = useAppSelector((state) => state.documents);
  const dashboardPageUpdating = useAppSelector((state) => state.loading[LoadingType.DashboardPageUpdating]);
  const dispatch = useAppDispatch();
  const documentsUploadDueDate = calculateDueDate(processingStatusSetDate);
  const isUploadingDocumentStep = DASHBOARD_STEPS_WITH_UPLOADING_DOCUMENTS.includes(currentDashboardStep!);
  const additionalTabProps = dashboardAdditionalInfo!;
  const isHilProduct = productName === ApplicationProductName.HomeImprovementLoan;

  const getCountIncomeSourceVerifications = (verificationState: VerificationState[]) => {
    let verificationsLeft = 0;

    verificationState.map((verification) => {
      if (verification.verificationRequired && !verification.verificationAttemptComplete) {
        verificationsLeft++;
      }
    });

    return verificationsLeft;
  };

  const handleCloseMessageFromHfsPopup = async (): Promise<void> => {
    const response = await applicationApi.markMessageFromHfsAsRead(applicationId!);

    updateApplicationInStore(response.application, dispatch);
  };

  const calculateAdditionalTabRemaining = useMemo(() => {
    const borrowerCurrentEmploymentRemaining = ~~!getIsEmploymentSectionCompleted(
      additionalTabProps,
      BorrowerRole.Borrower,
    );

    const contractorEmploymentRemaining =
      additionalTabProps.isContractorEmploymentRequired && !additionalTabProps.isMissingEmploymentInformationRequired
        ? ~~!additionalTabProps.isContractorEmploymentCompleted
        : 0;

    if (applicationType === ApplicationType.Joint) {
      const coBorrowerCurrentEmploymentRemaining = ~~!getIsEmploymentSectionCompleted(
        additionalTabProps,
        BorrowerRole.CoBorrower,
      );

      return borrowerCurrentEmploymentRemaining + coBorrowerCurrentEmploymentRemaining + contractorEmploymentRemaining;
    }

    return borrowerCurrentEmploymentRemaining + contractorEmploymentRemaining;
  }, [applicationType, dashboardAdditionalInfo]);

  const tabsPanelUploadDocsAndRequireInfo: Tab[] = useMemo(() => {
    const changeTab = async (step: DashboardStep, name: DashboardTabName) => {
      dispatch(setDashboardStep(step));
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${name}`,
          loading: true,
        }),
      );
      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${name}`,
          loading: false,
        }),
      );
    };

    const getBorrowerInfo = (borrowerType: BorrowerType): BorrowerInfo => {
      return {
        isInformationRequired:
          borrowerType === BorrowerType.Borrower ? borrowerInformationRequired : coBorrowerInformationRequired,
        payrollProviderIsDisabled:
          borrowerType === BorrowerType.Borrower
            ? borrowerPayrollProviderIsDisabled
            : coBorrowerPayrollProviderIsDisabled,
        payrollProviderVerificationStates:
          borrowerType === BorrowerType.Borrower
            ? payrollProviderBorrowerVerificationStates
            : payrollProviderCoBorrowerVerificationStates,
        employmentInformationComplete:
          borrowerType === BorrowerType.Borrower
            ? borrowerEmploymentInformationComplete
            : coBorrowerEmploymentInformationComplete,
      };
    };

    const countRemainingDocumentsForBorrowerInfo = (borrowerType: BorrowerType) => {
      const isBorrower = borrowerType === BorrowerType.Borrower;
      const borrowerInfo = getBorrowerInfo(borrowerType);

      if (!borrowerInfo.isInformationRequired) {
        return 0;
      }

      const isIncomeWaterfallApplied =
        applicationVariables?.income_waterfall_applied || applicationVariables?.income_waterfall_completed;

      const isIncomeWaterfallPayrollProviderVisible =
        borrowerType === BorrowerType.Borrower
          ? applicationVariables?.borrower_income_waterfall_payroll_provider_visible
          : applicationVariables?.coborrower_income_waterfall_payroll_provider_visible;

      const isIncomeWaterfallDocUploadVisible =
        borrowerType === BorrowerType.Borrower
          ? applicationVariables?.borrower_income_waterfall_doc_upload_visible
          : applicationVariables?.coborrower_income_waterfall_doc_upload_visible;

      let incomeSourceVerificationsCount = 0;

      if (!isIncomeWaterfallApplied || !!isIncomeWaterfallPayrollProviderVisible) {
        incomeSourceVerificationsCount =
          isTruvDisabled || borrowerInfo.payrollProviderIsDisabled
            ? 0
            : getCountIncomeSourceVerifications(borrowerInfo.payrollProviderVerificationStates);
      }

      const requiredBorrowerDocumentsCount =
        !isIncomeWaterfallApplied || !!isIncomeWaterfallDocUploadVisible
          ? getCountBorrowerRequiredDocuments(requiredDocuments, borrowerType)
          : getCountNotCompletedRequiredDocuments(
              requiredDocuments,
              borrowerType === BorrowerType.Borrower ? DocumentCategory.BorrowerID : DocumentCategory.CoborrowerID,
            );

      const borrowerIncomeInformationCompleteVariableName = isBorrower
        ? 'borrower_income_information_complete'
        : 'coborrower_income_information_complete';

      const isIncomeInformationComplete = !!applicationVariables?.[borrowerIncomeInformationCompleteVariableName];

      let smallLoanProgramEmploymentsInfoCount = 0;

      if (productName === ApplicationProductName.SmallLoanProgram) {
        const isProofOfIncomeRequired = isBorrower
          ? applicationVariables?.bor_proof_of_income_required
          : applicationVariables?.cob_proof_of_income_required;

        smallLoanProgramEmploymentsInfoCount =
          !isIncomeInformationComplete || isProofOfIncomeRequired || borrowerInfo.employmentInformationComplete ? 0 : 1;
      }

      const incomeTypesRemainingCount = isIncomeInformationComplete ? 0 : 1;

      const isKbaRequired = isBorrower
        ? applicationVariables?.borrower_kba_required
        : applicationVariables?.coborrower_kba_required;
      const kbaStatus = isBorrower
        ? applicationVariables?.borrower_kba_status
        : applicationVariables?.coborrower_kba_status;
      const isKbaAvailable = ![KbaStatus.Unavailable, KbaStatus.Expired].includes(kbaStatus!);
      const isKbaCompleted = [KbaStatus.Passed, KbaStatus.Failed].includes(kbaStatus!);
      const kbaRemainingCount = isKbaRequired && isKbaAvailable && !isKbaCompleted ? 1 : 0;

      return (
        incomeSourceVerificationsCount +
        requiredBorrowerDocumentsCount +
        smallLoanProgramEmploymentsInfoCount +
        incomeTypesRemainingCount +
        kbaRemainingCount
      );
    };

    return [
      {
        id: DashboardStep.BorrowerInformation,
        name: `${firstName}'s Info`,
        isExist: !!borrowerInformationRequired || isHilProduct,
        onClick: async () => changeTab(DashboardStep.BorrowerInformation, DashboardTabName.BorrowerInformation),
        countRemaining: countRemainingDocumentsForBorrowerInfo(BorrowerType.Borrower),
        isDisabled: false,
      },
      {
        id: DashboardStep.CoBorrowerInformation,
        name: `${coBorrowerFirstName}'s Info`,
        isExist: applicationType === ApplicationType.Joint && (!!coBorrowerInformationRequired || isHilProduct),
        onClick: async () => changeTab(DashboardStep.CoBorrowerInformation, DashboardTabName.CoBorrowerInformation),
        countRemaining: countRemainingDocumentsForBorrowerInfo(BorrowerType.CoBorrower),
        isDisabled: false,
      },
      {
        id: DashboardStep.HomeOwnership,
        name: 'Homeownership',
        isExist: true,
        onClick: async () => changeTab(DashboardStep.HomeOwnership, DashboardTabName.HomeOwnership),
        countRemaining:
          getCountNotCompletedRequiredDocuments(requiredDocuments, DocumentCategory.HomeVerification) +
          (isThereNonBorrowingCoOwner ? +!isCoOwnerInformationComplete : 0) +
          +!isHomeOwnershipInformationComplete,
        isDisabled: false,
      },
      {
        id: DashboardStep.ContractorWorkOrder,
        name: 'Work Order',
        isExist: isRequiredDocumentsBelongsToCategory(requiredDocuments, DocumentCategory.ContractorWorkOrder),
        onClick: async () => changeTab(DashboardStep.ContractorWorkOrder, DashboardTabName.WorkOrder),
        countRemaining: getCountNotCompletedRequiredDocuments(requiredDocuments, DocumentCategory.ContractorWorkOrder),
        isDisabled: false,
      },
      {
        id: DashboardStep.FundingInformation,
        name: 'Funding',
        tooltipTitle: DISABLED_FUNDING_TOOLTIP_TEXT,
        isExist:
          fundingInformationIsRequired ||
          autopaySectionIsRequired ||
          isRequiredDocumentsBelongsToCategory(requiredDocuments, DocumentCategory.Funding),
        onClick: async () => {
          dispatch(setDashboardStep(DashboardStep.FundingInformation));
        },
        countRemaining:
          (fundingInformationIsRequired ? ~~!fundingInformationComplete : 0) +
          getCountNotCompletedRequiredDocuments(requiredDocuments, DocumentCategory.Funding) +
          (autopaySectionIsRequired ? ~~!autopaySectionIsCompleted : 0),
        isDisabled:
          !isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.BorrowerID) ||
          (applicationType === ApplicationType.Joint
            ? !isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.CoborrowerID)
            : false),
      },
      {
        id: DashboardStep.AdditionalInformation,
        name: 'Additional Info',
        isExist: additionalTabProps.isAdditionalInfoTabRequired,
        onClick: async () => changeTab(DashboardStep.AdditionalInformation, DashboardTabName.AdditionalInformation),
        countRemaining: calculateAdditionalTabRemaining,
        isDisabled: false,
      },
      {
        id: DashboardStep.ContractorInfo,
        name: 'Contractor Info',
        isExist: productName === ApplicationProductName.SmallLoanProgram,
        onClick: async () => changeTab(DashboardStep.ContractorInfo, DashboardTabName.ContractorInfo),
        countRemaining: ~~!contractorInformationComplete,
        isDisabled: false,
      },
    ];
  }, [
    firstName,
    borrowerInformationRequired,
    coBorrowerFirstName,
    applicationType,
    coBorrowerInformationRequired,
    isHilProduct,
    requiredDocuments,
    isThereNonBorrowingCoOwner,
    isHomeOwnershipInformationComplete,
    fundingInformationIsRequired,
    fundingInformationComplete,
    contractorInformationComplete,
    autopaySectionIsRequired,
    autopaySectionIsCompleted,
    productName,
    additionalTabProps.isAdditionalInfoTabRequired,
    calculateAdditionalTabRemaining,
    dispatch,
    borrowerId,
    borrowerPayrollProviderIsDisabled,
    coBorrowerPayrollProviderIsDisabled,
    payrollProviderBorrowerVerificationStates,
    payrollProviderCoBorrowerVerificationStates,
    borrowerEmploymentInformationComplete,
    coBorrowerEmploymentInformationComplete,
    applicationVariables,
    isTruvDisabled,
  ]);

  const setDashboardStepByStatus = useCallback(() => {
    if (currentDashboardStep !== null) {
      return;
    }

    switch (true) {
      case applicationStatus === ApplicationStatus.Active:
        return dispatch(setDashboardStep(DashboardStep.NewInquiry));
      case dashboardAdditionalInfo?.isProsperJointRedirectView:
        return dispatch(setDashboardStep(DashboardStep.ProsperJointTransition));
      // Automated offers stay reachable from the Dashboard for applications already pitched in the LOS —
      // e.g. an application moved from HIL to POS manually after the borrower finished prequalification.
      case applicationStatus === ApplicationStatus.Pitched &&
        productName === ApplicationProductName.SmallLoanProgram &&
        applicationVariables!.hasPosAutomatedOffer:
        return dispatch(setDashboardStep(DashboardStep.AcceptPosAutomatedOffer));
      case applicationStatus === ApplicationStatus.Pitched &&
        productName === ApplicationProductName.HomeImprovementLoan &&
        applicationVariables!.hasAutomatedOffer:
        return dispatch(setDashboardStep(DashboardStep.AcceptHilAutomatedOffer));
      case applicationStatus === ApplicationStatus.Pitched && applicationVariables!.multiple_loan_offers:
        return dispatch(setDashboardStep(DashboardStep.AcceptManualMultiTermOffer));
      case applicationStatus === ApplicationStatus.Pitched && !applicationVariables!.multiple_loan_offers:
        return dispatch(setDashboardStep(DashboardStep.AcceptManualOneTermOffer));
      case applicationStatus === ApplicationStatus.Purgatory:
      case applicationStatus === ApplicationStatus.Processing:
      case applicationStatus === ApplicationStatus.ReadyToSubmit:
      case applicationStatus === ApplicationStatus.Underwriting:
      case applicationStatus === ApplicationStatus.NeedMoreInfo:
      case applicationStatus === ApplicationStatus.NMICompleted:
      case applicationStatus === ApplicationStatus.PendingFunding:
      case applicationStatus === ApplicationStatus.Compliance:
      case applicationStatus === ApplicationStatus.Approved: {
        if (dashboardAdditionalInfo?.isProsperView) {
          return dispatch(setDashboardStep(DashboardStep.ProsperLoan));
        }

        const firstAvailableTab = tabsPanelUploadDocsAndRequireInfo.find((tabInfo) => tabInfo.isExist);

        // set first available Dashboard step for uploading document
        if (!isUploadingDocumentStep && firstAvailableTab) {
          return dispatch(setDashboardStep(firstAvailableTab.id));
        }

        return;
      }
      default:
        return dispatch(setDashboardStep(DashboardStep.NoActiveApplication));
    }
  }, [
    applicationStatus,
    currentDashboardStep,
    dispatch,
    isUploadingDocumentStep,
    productName,
    tabsPanelUploadDocsAndRequireInfo,
    dashboardAdditionalInfo?.isProsperView,
    dashboardAdditionalInfo?.isProsperJointRedirectView,
  ]);

  const getSidebarAdditionInfo = () => {
    if (productName === ApplicationProductName.SmallLoanProgram) {
      return applicationStatus === ApplicationStatus.Pitched ? (
        <InformationalBoxWithBubble>
          <LoanOfferOptions />
        </InformationalBoxWithBubble>
      ) : null;
    } else {
      return isUploadingDocumentStep ? (
        <InformationalBoxWithBubble>
          <VerificationBoxPrompt />
        </InformationalBoxWithBubble>
      ) : null;
    }
  };

  const getOnClickEvent = (sidebarElement: DashboardSidebarElements) => {
    switch (sidebarElement) {
      case DashboardSidebarElements.MyApplication:
        return () => {
          dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: true }));
          dispatch(getApplicationInfoForDashboardThunk(borrowerId!))
            .unwrap()
            .finally(() => {
              dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: false }));
            });
          dispatch(setDashboardStep(null));
        };

      case DashboardSidebarElements.FAQs:
        return () => {
          if (applicationId) {
            dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
          }

          dispatch(setDashboardStep(DashboardStep.FAQ));
        };

      case DashboardSidebarElements.AccountSettings:
        return async () => {
          dispatch(setDashboardStep(DashboardStep.AccountSettings));
          dispatch(setLoadingState({ type: LoadingType.AccountSettingUpdating, loading: true }));

          const account = await dispatch(getAccount()).unwrap();

          dispatch(setAccount({ email: account?.email, phone: account?.phones[0]?.value }));

          dispatch(setLoadingState({ type: LoadingType.AccountSettingUpdating, loading: false }));
        };

      case DashboardSidebarElements.LoanDetails:
        return async () => {
          dispatch(setDashboardStep(DashboardStep.LoanDetails));
          dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: true }));
          await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
          dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: false }));
        };

      default:
        return () => {};
    }
  };

  const getActiveSidebarElement = (step: DashboardStep | null) => {
    switch (step) {
      case DashboardStep.FAQ:
        return DashboardSidebarElements.FAQs;
      case DashboardStep.AccountSettings:
        return DashboardSidebarElements.AccountSettings;
      case DashboardStep.LoanDetails:
        return DashboardSidebarElements.LoanDetails;

      default:
        return DashboardSidebarElements.MyApplication;
    }
  };

  useEffect(() => {
    setDashboardStepByStatus();
  }, [setDashboardStepByStatus]);

  const handleWithdrawApplicationClick = () => {
    setIsDeclineOfferOpen(true);
  };

  const handleClosePopup = () => {
    setIsDeclineOfferOpen(false);
  };

  if (isIframe) {
    return <NewInquiry />;
  }

  return (
    <div className={styles.dashboardContainer}>
      <Sidebar
        sidebarElements={
          Object.values(DashboardSidebarElements)
            .filter((sidebarElement) => {
              if (sidebarElement === DashboardSidebarElements.LoanDetails) {
                return applicationStatus && STATUSES_FOR_LOAN_DETAILS.includes(applicationStatus);
              }

              return true;
            })
            .map((sidebarElement) => ({
              name: sidebarElement,
              onClick: getOnClickEvent(sidebarElement),
            })) as SidebarElement[]
        }
        activeElement={getActiveSidebarElement(currentDashboardStep)}
        borrowerName={`${firstName} ${lastName}`}
        borrowerEmail={email}
        additionalInfo={getSidebarAdditionInfo()}
      >
        {STATUSES_FOR_WITHDRAW_APPLICATION.includes(applicationStatus!) && (
          <Button
            onClick={handleWithdrawApplicationClick}
            className={styles.buttonWithdrawApplication}
            buttonType={ButtonType.Secondary}
          >
            {WITHDRAW_APPLICATION}
          </Button>
        )}
      </Sidebar>
      <div
        className={clsx(
          styles.dashboardContentContainer,
          currentDashboardStep === DashboardStep.AccountSettings && styles.overflowHidden,
        )}
      >
        {![DashboardStep.AccountSettings, DashboardStep.NoActiveApplication].includes(currentDashboardStep!) && (
          <DashboardTopInfo
            applicationId={displayId}
            loanAmount={loanAmount}
            label={getCurrentLabel(applicationStatus)}
            createdDate={createdDate}
            processorInfo={processorInfo}
          />
        )}
        {getDashboardFormHeader(
          currentDashboardStep,
          messageFromHFS,
          documentsUploadDueDate,
          tabsPanelUploadDocsAndRequireInfo,
        )}
        {dashboardPageUpdating ? <Loader /> : getPageByStep(currentDashboardStep)}
      </div>
      <DeclineOfferPopup isModalOpen={isDeclineOfferOpen} onClose={handleClosePopup} applicationId={applicationId!} />
      <MessageFromHfsPopup
        isOpen={messageFromHfsShowOnPortal || false}
        mainMessage={messageFromHFS!}
        onClose={handleCloseMessageFromHfsPopup}
      />
    </div>
  );
};

export default Dashboard;
